import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, ArrowRight, Sparkles, Clock, GraduationCap, Lock,
  CheckCircle2, Trophy, Award,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { ETM_ISSUES_INDEX } from '../data/etmIssues';
import api from '../api';
import { useApp } from '../contexts/AppContext';

const BADGES_META = {
  'lecteur-etm-accompli': {
    label: 'Lecteur accompli',
    desc: 'Tu as terminé les 9 numéros du programme',
    color: '#cf2031',
    icon: Trophy,
  },
  'lecteur-etm-assidu': {
    label: 'Lecteur assidu',
    desc: '8 numéros lus sur 9',
    color: '#0891b2',
    icon: Award,
  },
};

export default function HomeMagazineEtmSection() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const isLogged = !!user;
  const publishedIssues = ETM_ISSUES_INDEX.filter((i) => i.status === 'published');
  const publishedCount = publishedIssues.length;
  // Hero = numéro pilote EPI (référence du concept) — pas forcément le dernier publié
  const publishedIssue = publishedIssues.find((i) => i.slug === 'epi') || publishedIssues[0];

  useEffect(() => {
    if (!isLogged) {
      setProgress(null);
      return;
    }
    let active = true;
    api.getEtmProgression()
      .then((data) => { if (active) setProgress(data); })
      .catch(() => { if (active) setProgress(null); });
    return () => { active = false; };
  }, [isLogged]);

  const issuesProgress = progress?.issues || {};
  const summary = progress?.summary || null;
  const badges = summary?.badges || [];
  const readCount = summary?.read ?? 0;
  const completedCount = summary?.completed ?? 0;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border bg-card"
      data-testid="home-magazine-etm"
      aria-labelledby="magazine-etm-title"
      style={{
        background:
          'radial-gradient(ellipse 60% 50% at 0% 0%, rgba(207, 32, 49, 0.06), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 100%, rgba(8, 145, 178, 0.05), transparent 60%), hsl(var(--card))',
      }}
    >
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 12px, hsl(var(--foreground)) 12px 13px)',
        }}
      />

      <div className="relative px-5 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12">
        {/* HEADER */}
        <div className="flex items-end justify-between gap-3 mb-5 sm:mb-7 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-primary">
                Nouveau · Programme pédagogique
              </span>
            </div>
            <h2
              id="magazine-etm-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight inline-flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
              Le Magazine pédagogique
            </h2>
            <p className="text-xs sm:text-sm text-foreground/60 mt-2 max-w-2xl">
              9 dossiers pédagogiques pour <strong>comprendre</strong> le 2 roues en profondeur, façon magazine.
              Chaque numéro = un thème, un récit éditorial à lire à votre rythme.
            </p>
          </div>

          {/* Compteur progression utilisateur */}
          {isLogged && summary && (
            <div
              className="rounded-lg border bg-card/40 p-3 flex items-center gap-3"
              data-testid="magazine-etm-progress"
            >
              <div className="text-right">
                <div className="text-xl font-black tabular-nums leading-none">
                  {completedCount}<span className="text-sm text-muted-foreground font-bold">/9</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-0.5">
                  lus
                </div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div className="text-right">
                <div className="text-xl font-black tabular-nums leading-none text-primary">
                  {readCount}<span className="text-sm text-muted-foreground font-bold">/9</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-0.5">
                  lus
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Badges utilisateur */}
        {isLogged && badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="magazine-etm-badges">
            {badges.map((b) => {
              const meta = BADGES_META[b];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <Badge
                  key={b}
                  className="text-[11px] gap-1.5 px-3 py-1 border-none"
                  style={{ backgroundColor: meta.color, color: '#fff' }}
                  data-testid={`magazine-etm-badge-${b}`}
                  title={meta.desc}
                >
                  <Icon className="w-3.5 h-3.5" /> {meta.label}
                </Badge>
              );
            })}
          </div>
        )}

        {/* HERO du numéro publié */}
        {publishedIssue && (
          <button
            type="button"
            onClick={() => navigate(`/apprendre/${publishedIssue.slug}`)}
            className="w-full text-left rounded-xl border-2 border-primary/30 overflow-hidden mb-5 sm:mb-6 group hover:border-primary hover:shadow-lg transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid={`magazine-etm-hero-${publishedIssue.slug}`}
            style={{
              background:
                'linear-gradient(135deg, rgba(207,32,49,0.06) 0%, rgba(8,145,178,0.04) 100%)',
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 p-4 sm:p-5 items-center">
              <div className="space-y-2.5 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground border-none text-[10px]">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Numéro pilote — Disponible maintenant
                  </Badge>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                    N°{publishedIssue.number}/9 · {publishedIssue.shortTitle}
                  </span>
                  {isLogged && issuesProgress[publishedIssue.slug]?.completed && (
                    <Badge className="text-[10px] bg-emerald-600 text-white border-none">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Validé
                    </Badge>
                  )}
                </div>
                <h3
                  className="text-lg sm:text-2xl font-extrabold tracking-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {publishedIssue.title}
                </h3>
                <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">
                  {publishedIssue.teaser}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-background/60">
                    <Clock className="w-3 h-3" /> 12 min
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-background/60">
                    <GraduationCap className="w-3 h-3" /> Format magazine
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-background/60">
                    <Sparkles className="w-3 h-3 text-amber-500" /> 100 % gratuit
                  </span>
                </div>
              </div>
              <div className="hidden sm:flex flex-col items-end gap-2 flex-shrink-0">
                <div className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground font-bold text-sm group-hover:bg-primary/90 transition-colors shadow-md">
                  {isLogged && issuesProgress[publishedIssue.slug]?.read ? 'Continuer' : 'Lire le numéro'} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>
            <div className="sm:hidden p-3 pt-0">
              <div className="inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground font-bold text-sm w-full">
                {isLogged && issuesProgress[publishedIssue.slug]?.read ? 'Continuer' : 'Lire le numéro'} <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </button>
        )}

        {/* GRILLE DES 9 NUMÉROS avec progression utilisateur */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3" data-testid="magazine-etm-grid">
          {ETM_ISSUES_INDEX.map((it) => {
            const isPublished = it.status === 'published';
            const userIssue = issuesProgress[it.slug];
            const isCompleted = isPublished && userIssue?.completed;
            const isRead = isPublished && userIssue?.read;

            return (
              <button
                key={it.slug}
                type="button"
                onClick={() => navigate(`/apprendre/${it.slug}`)}
                className={`group text-left p-2.5 sm:p-3 rounded-lg border transition-all hover:-translate-y-0.5 active:translate-y-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isCompleted
                    ? 'border-emerald-600/60 bg-emerald-500/5 hover:border-emerald-600'
                    : isPublished
                      ? 'border-primary/40 bg-card hover:border-primary hover:shadow-md'
                      : 'border-dashed border-border bg-muted/20 hover:border-foreground/30'
                }`}
                data-testid={`magazine-etm-card-${it.slug}`}
                aria-label={`Numéro ${it.number} · ${it.shortTitle}${isPublished ? ' (disponible)' : ' (à venir)'}${isCompleted ? ' (validé)' : ''}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span
                    className="font-extrabold text-base sm:text-lg leading-none tabular-nums"
                    style={{ color: isPublished ? it.accent : 'hsl(var(--muted-foreground))' }}
                  >
                    N°{it.number}
                  </span>
                  {isCompleted ? (
                    <Badge className="text-[9px] h-4 px-1 bg-emerald-600 text-white border-none ml-auto inline-flex items-center gap-0.5">
                      <CheckCircle2 className="w-2.5 h-2.5" /> Validé
                    </Badge>
                  ) : isRead ? (
                    <Badge className="text-[9px] h-4 px-1 bg-primary/15 text-primary border border-primary/30 ml-auto">
                      Lu
                    </Badge>
                  ) : isPublished ? (
                    <Badge className="text-[9px] h-4 px-1 bg-emerald-600 text-white border-none ml-auto">
                      Lire
                    </Badge>
                  ) : (
                    <Lock className="w-3 h-3 text-muted-foreground ml-auto" />
                  )}
                </div>
                <div className="text-[11px] sm:text-xs font-bold leading-tight line-clamp-1">
                  {it.shortTitle}
                </div>
                <div className="text-[10px] sm:text-[11px] text-foreground/55 leading-snug mt-0.5 line-clamp-2">
                  {it.teaser}
                </div>
                {isPublished && userIssue?.best_score !== undefined && (
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold mt-1 tabular-nums">
                    Meilleur score : {userIssue.best_score}/{userIssue.last_total || 10}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-center text-[11px] text-muted-foreground gap-1.5">
          <span>{publishedCount} numéro{publishedCount > 1 ? 's' : ''} publié{publishedCount > 1 ? 's' : ''} sur 9 · Les {9 - publishedCount} autres arrivent prochainement.</span>
        </div>
      </div>
    </section>
  );
}
